// components/DegreeAudit/Common/ConstraintWarnings.tsx
//
// W22 §22.7 frontend half. Renders the audit response's `warnings: []`
// field — the inert-constraint warnings surfaced by the 4 Phase 4
// readers (gpa / residency / gateway / course_set_caps).
//
// Distinct from <ConstraintViolations/> by intent + visual treatment:
//   - ConstraintViolations: HARD failures (missing prereqs, mutual
//     exclusions). Red theme. Audit cannot be considered complete.
//   - ConstraintWarnings: SOFT warnings (GPA shortfall, residency gap,
//     gateway course missing, course-set cap exceeded). Amber theme.
//     The audit can still pass overall; warnings inform the student.

import { memo, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

export interface ConstraintWarning {
  primitive: 'gpa' | 'residency' | 'gateway' | 'course_set_caps';
  scope: string;
  message: string;
  // expected/actual are deliberately flexible — GPA returns floats,
  // residency returns ints, gateway returns string[] (required codes),
  // course_set_caps returns either count or credit totals.
  expected?: unknown;
  actual?: unknown;
  source?: 'program' | 'institution_default';
}

export interface ConstraintWarningsProps {
  warnings: ConstraintWarning[] | null | undefined;
}

const PRIMITIVE_LABELS: Record<ConstraintWarning['primitive'], string> = {
  gpa: 'GPA',
  residency: 'Residency',
  gateway: 'Gateway / Milestone',
  course_set_caps: 'Course-Set Limits',
};

const ConstraintWarnings = memo(({ warnings }: ConstraintWarningsProps) => {
  // Collapsed by default — these are soft, advisory warnings (distinct from
  // the red hard-failure violations). A count badge keeps them discoverable
  // without occupying prime real estate above the actual degree progress.
  const [expanded, setExpanded] = useState(false);

  if (!warnings || warnings.length === 0) {
    return null;
  }

  // Group by primitive so the user sees one section per category.
  const grouped = warnings.reduce<Record<string, ConstraintWarning[]>>(
    (acc, w) => {
      const key = w.primitive;
      if (!acc[key]) acc[key] = [];
      acc[key].push(w);
      return acc;
    },
    {}
  );

  return (
    <div className="bg-status-warning/10 border border-status-warning/25 rounded-card p-3 md:p-4">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center text-status-warning-fg font-medium text-sm md:text-base"
      >
        <AlertTriangle className="w-4 md:w-5 h-4 md:h-5 mr-1 md:mr-2 print-hide" />
        Audit Warnings
        <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-status-warning/25 text-status-warning-fg text-xs">
          {warnings.length}
        </span>
        <span className="ml-auto print-hide">
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>

      <div className={expanded ? 'mt-2 md:mt-3' : 'hidden print:block print:mt-3'}>
        {Object.keys(PRIMITIVE_LABELS).map((primitive) => {
          const list = grouped[primitive];
          if (!list || list.length === 0) return null;

          return (
            <div key={primitive} className="mb-3 md:mb-4 last:mb-0">
              <h4 className="font-medium text-status-warning-fg mb-1 md:mb-2 text-sm md:text-base">
                {PRIMITIVE_LABELS[primitive as ConstraintWarning['primitive']]}:
              </h4>
              <ul className="space-y-1 text-xs md:text-sm text-status-warning-fg">
                {list.map((w, idx) => (
                  <li key={idx} className="flex items-start">
                    <span className="mr-1 md:mr-2">•</span>
                    <span>
                      {w.message}
                      {w.source === 'institution_default' && (
                        <span className="ml-2 text-xs italic text-amber-600">
                          (institution-wide default)
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
});

ConstraintWarnings.displayName = 'ConstraintWarnings';

export default ConstraintWarnings;
